import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Checkbox } from "../components/ui/checkbox";
import { Badge } from "../components/ui/badge";
import SearchableSelect from "../components/ui/SearchableSelect";
import PhoneInput from "../components/ui/PhoneInput";
import { formatPhone } from "../utils/phoneUtils";
import { Trash2, Plus, FileText, Upload, X } from "lucide-react";
import { customerService } from "../services/customerService";
import { projectService } from "../services/projectService";
import { estimateService } from "../services/estimateService";
import { designerService } from "../services/designerService";
import { workItemService } from "../services/workItemService";
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useToast } from "../hooks/use-toast";

const escapeHtml = (str) => {
  if (!str) return '';
  return String(str).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
};

export default function EstimateCreate() {
  const navigate = useNavigate();
  const { estimateId } = useParams();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const isEditMode = !!estimateId;
  
  // State for tabs
  const [activeTab, setActiveTab] = useState("client");
  
  // State for form data
  const [estimateData, setEstimateData] = useState({
    project_name: "",
    client_name: "",
    client_phone: "",
    client_email: "",
    job_address: "",
    client_notes: "",
    designer_name: "",
    designer_email: "",
    designer_phone: "",
    designer_his: "",
    first_responder: false,
    work_items: [],
    design_images: []
  });

  // API hooks
  const { data: customersData } = useQuery({
    queryKey: ['customers'],
    queryFn: () => customerService.getAll()
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectService.getAll()
  });

  const { data: designersData } = useQuery({
    queryKey: ['designers'],
    queryFn: () => designerService.getAll()
  });

  const { data: workItemsData, isLoading: workItemsLoading } = useQuery({
    queryKey: ['work-items'],
    queryFn: () => workItemService.getAll({ active_only: true, limit: 1000 })
  });

  // Fetch estimate if editing
  const { data: existingEstimate, isLoading: estimateLoading } = useQuery({
    queryKey: ['estimate', estimateId],
    queryFn: () => estimateService.getById(estimateId),
    enabled: isEditMode
  });

  // Load estimate data into form when fetched
  useEffect(() => {
    // Only proceed if we're in edit mode, have data, and not loading
    if (existingEstimate && isEditMode && !estimateLoading) {
      console.log("Raw existing estimate data:", existingEstimate);
      
      // API responses are wrapped in a data field
      const estimate = existingEstimate.data || existingEstimate;
      console.log("Unwrapped estimate data:", estimate);
      
      // Verify we have actual data
      if (!estimate.project_name) {
        console.error("No project_name in estimate data - data might not be unwrapped correctly");
        return;
      }
      
      // Ensure work_items have all required fields (for backward compatibility)
      const work_items = (estimate.work_items || []).map(item => ({
        ...item,
        selected_id: item.selected_id || null,
        internal_name: item.internal_name || item.name,
        commission: item.commission || 0,
        labor: item.labor || 0
      }));
      
      console.log("Setting estimate data with work items:", work_items);
      
      setEstimateData({
        project_name: estimate.project_name || "",
        client_name: estimate.client_name || "",
        client_phone: estimate.client_phone || "",
        client_email: estimate.client_email || "",
        job_address: estimate.job_address || "",
        client_notes: estimate.client_notes || "",
        designer_name: estimate.designer_name || "",
        designer_email: estimate.designer_email || "",
        designer_phone: estimate.designer_phone || "",
        designer_his: estimate.designer_his || "",
        first_responder: estimate.first_responder || false,
        work_items: work_items,
        design_images: estimate.design_images || []
      });
      
      // Set related dropdowns
      if (estimate.customer_id) {
        setSelectedCustomer(estimate.customer_id);
      }
      if (estimate.project_id) {
        setSelectedProject(estimate.project_id);
      }
      if (estimate.designer_name) {
        setSelectedDesigner(estimate.designer_name);
      }
      
      console.log("✅ Estimate data loaded successfully");
    }
  }, [existingEstimate, isEditMode, estimateLoading]);

  const createEstimateMutation = useMutation({
    mutationFn: (data) => estimateService.create(data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      toast({ description: "Estimate created successfully!" });
      navigate('/estimates');
    },
    onError: (error) => {
      // Handle validation errors from backend
      let errorMessage = "Failed to create estimate";
      
      if (error?.detail) {
        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (Array.isArray(error.detail)) {
          // Pydantic validation errors are arrays of objects
          errorMessage = error.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof error.detail === 'object') {
          errorMessage = JSON.stringify(error.detail);
        }
      }
      
      toast({
        variant: "destructive",
        description: errorMessage
      });
    }
  });

  const updateEstimateMutation = useMutation({
    mutationFn: (data) => estimateService.update(estimateId, data),
    onSuccess: (response) => {
      queryClient.invalidateQueries({ queryKey: ['estimates'] });
      queryClient.invalidateQueries({ queryKey: ['estimate', estimateId] });
      toast({ description: "Estimate updated successfully!" });
      navigate('/estimates');
    },
    onError: (error) => {
      // Handle validation errors from backend
      let errorMessage = "Failed to update estimate";
      
      if (error?.detail) {
        if (typeof error.detail === 'string') {
          errorMessage = error.detail;
        } else if (Array.isArray(error.detail)) {
          errorMessage = error.detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof error.detail === 'object') {
          errorMessage = JSON.stringify(error.detail);
        }
      }
      
      toast({
        variant: "destructive",
        description: errorMessage
      });
    }
  });

  // Dialog states
  const [showAddItemDialog, setShowAddItemDialog] = useState(false);
  const [showPreviewDialog, setShowPreviewDialog] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedProject, setSelectedProject] = useState("");
  const [selectedDesigner, setSelectedDesigner] = useState("");

  // Work item form state
  const [newWorkItem, setNewWorkItem] = useState({
    selected_id: "",
    name: "",
    internal_name: "",
    category: "",
    quantity: 1,
    unit: "ea.",
    price: 0,
    base_price: 0, // Minimum price from database (for predefined items)
    commission: 0,
    labor: 0,
    is_custom: false
  });

  // Get work items from database
  const predefinedItems = workItemsData?.data || [];

  // Handle customer selection
  const handleCustomerChange = (customerId) => {
    setSelectedCustomer(customerId);
    if (customerId && customerId !== "none" && customersData?.data) {
      const customer = customersData.data.find(c => (c.id || c._id) === customerId);
      if (customer) {
        // Combine first name and last name
        const fullName = `${customer.name || ''} ${customer.last_name || ''}`.trim();
        
        setEstimateData(prev => ({
          ...prev,
          client_name: fullName || customer.name || "",
          client_phone: customer.phone || "",
          client_email: customer.email || "", 
          job_address: customer.address || ""
        }));
      }
    } else {
      // Reset to empty when "none" is selected
      setEstimateData(prev => ({
        ...prev,
        client_name: "",
        client_phone: "",
        client_email: "",
        job_address: ""
      }));
    }
  };

  // Handle project selection
  const handleProjectChange = (projectId) => {
    setSelectedProject(projectId);
    if (projectId && projectId !== "none" && projectsData?.data) {
      const project = projectsData.data.find(p => (p.id || p._id) === projectId);
      if (project) {
        setEstimateData(prev => ({
          ...prev,
          project_name: project.name
        }));
      }
    }
  };

  // Handle designer selection
  const handleDesignerChange = (designerId) => {
    setSelectedDesigner(designerId);
    if (designerId && designerId !== "none" && designersData?.data) {
      const designer = designersData.data.find(d => (d.id || d._id) === designerId);
      if (designer) {
        setEstimateData(prev => ({
          ...prev,
          designer_name: designer.name || "",
          designer_email: designer.email || "",
          designer_phone: designer.phone || "",
          designer_his: designer.his_license_number || ""
        }));
      }
    }
  };

  // Add work item
  const addWorkItem = () => {
    const item = {
      ...newWorkItem,
      id: String(Date.now()), // Temporary ID as string
      selected: true,
      line_total: newWorkItem.quantity * newWorkItem.price
    };
    
    setEstimateData(prev => ({
      ...prev,
      work_items: [...prev.work_items, item]
    }));
    
    // Reset form
    setNewWorkItem({
      selected_id: "",
      name: "",
      internal_name: "",
      category: "",
      quantity: 1,
      unit: "ea.",
      price: 0,
      base_price: 0,
      commission: 0,
      labor: 0,
      is_custom: false
    });
    setShowAddItemDialog(false);
  };

  // Remove work item
  const removeWorkItem = (itemId) => {
    setEstimateData(prev => ({
      ...prev,
      work_items: prev.work_items.filter(item => item.id !== itemId)
    }));
  };

  // Toggle work item selection
  const toggleItemSelection = (itemId) => {
    setEstimateData(prev => ({
      ...prev,
      work_items: prev.work_items.map(item =>
        item.id === itemId 
          ? { ...item, selected: !item.selected }
          : item
      )
    }));
  };

  // Update work item
  const updateWorkItem = (itemId, field, value) => {
    setEstimateData(prev => ({
      ...prev,
      work_items: prev.work_items.map(item => {
        if (item.id === itemId) {
          const updated = { ...item, [field]: value };
          if (field === 'quantity' || field === 'price') {
            updated.line_total = updated.quantity * updated.price;
          }
          return updated;
        }
        return item;
      })
    }));
  };

  // Calculate totals
  const calculateTotals = () => {
    const selectedItems = estimateData.work_items.filter(item => item.selected);
    const subtotal = selectedItems.reduce((sum, item) => sum + item.line_total, 0);
    
    // Apply discounts
    let discounts = [];
    
    // Design Creation Discount (80% off)
    const designItem = selectedItems.find(item => 
      item.name.toLowerCase().includes('design creation')
    );
    if (designItem) {
      const discount = designItem.line_total * 0.80;
      discounts.push({
        label: "Design Creation Discount (80%)",
        amount: -discount
      });
    }
    
    // First Responder Discount (3%)
    if (estimateData.first_responder) {
      const discount = subtotal * 0.03;
      discounts.push({
        label: "First Responders Discount (3%)",
        amount: -discount
      });
    }
    
    const discountTotal = discounts.reduce((sum, d) => sum + d.amount, 0);
    const total = subtotal + discountTotal;
    
    return { subtotal, discounts, total };
  };

  // Handle design image upload
  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    const imagePromises = files.map(file => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = (e) => resolve(e.target.result);
        reader.readAsDataURL(file);
      });
    });
    
    Promise.all(imagePromises).then(images => {
      setEstimateData(prev => ({
        ...prev,
        design_images: [...prev.design_images, ...images]
      }));
    });
  };

  // Remove design image
  const removeDesignImage = (index) => {
    setEstimateData(prev => ({
      ...prev,
      design_images: prev.design_images.filter((_, i) => i !== index)
    }));
  };

  // Generate and download PDF
  const handleExportPDF = async () => {
    try {
      // Check if estimate has required data
      if (!estimateData.project_name || estimateData.work_items.filter(i => i.selected).length === 0) {
        toast({
          variant: "destructive",
          description: "Please add a project name and select at least one work item before exporting PDF"
        });
        return;
      }

      // Show loading state
      toast({
        description: "Generating PDF..."
      });

      let pdfEstimateId;

      // If in edit mode, use existing estimate ID
      if (isEditMode && estimateId) {
        pdfEstimateId = estimateId;
      } else {
        // Create estimate in database first for PDF generation
        const { subtotal, discounts, total } = calculateTotals();
        
        const estimatePayload = {
          project_name: estimateData.project_name || "Untitled Project",
          client_name: estimateData.client_name || "",
          customer_id: selectedCustomer && selectedCustomer !== "none" ? selectedCustomer : null,
          project_id: selectedProject && selectedProject !== "none" ? selectedProject : null,
          client_phone: estimateData.client_phone || "",
          client_email: estimateData.client_email || "",
          job_address: estimateData.job_address || "",
          client_notes: estimateData.client_notes || "",
          designer_name: estimateData.designer_name || "",
          designer_email: estimateData.designer_email || "",
          designer_phone: estimateData.designer_phone || "",
          designer_his: estimateData.designer_his || "",
          first_responder: estimateData.first_responder || false,
          work_items: estimateData.work_items,
          design_images: estimateData.design_images || [],
          subtotal,
          discount_amount: discounts.reduce((sum, d) => sum + Math.abs(d.amount), 0),
          total,
          status: "draft"
        };

        // Save estimate temporarily
        const savedEstimate = await estimateService.create(estimatePayload);
        pdfEstimateId = savedEstimate.id;
      }

      // Now generate PDF
      try {
        // Get backend URL and token
        const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
        const token = localStorage.getItem('auth_token');

        // Fetch PDF from backend
        const response = await fetch(`${backendUrl}/api/estimates/${pdfEstimateId}/pdf`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        
        if (!response.ok) {
          throw new Error('Failed to generate PDF');
        }
        
        // Get the PDF blob
        const blob = await response.blob();
        
        // Create download link
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        
        // Extract filename from Content-Disposition header or use default
        const contentDisposition = response.headers.get('Content-Disposition');
        let filename = `Estimate_${estimateData.project_name || 'Untitled'}_${new Date().toISOString().slice(0, 10)}.pdf`;
        
        if (contentDisposition) {
          const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
          if (filenameMatch && filenameMatch[1]) {
            filename = filenameMatch[1].replace(/['"]/g, '');
          }
        }
        
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);
        
        toast({
          description: "PDF downloaded successfully!"
        });
      } catch (pdfError) {
        console.error("Failed to generate PDF:", pdfError);
        toast({
          variant: "destructive",
          description: "Failed to generate PDF. Please try again."
        });
      }
      
    } catch (error) {
      console.error("Failed to save estimate for PDF:", error);
      toast({
        variant: "destructive",
        description: "Failed to save estimate. Please try again."
      });
    }
  };

  // OLD PRINT DIALOG VERSION - REPLACED WITH WEASYPRINT
  const handleExportPDF_OLD = async () => {
    try {
      // Create a temporary div with the proposal content
      const pdfContent = document.createElement('div');
      pdfContent.innerHTML = `
        <div style="font-family: Arial, sans-serif; padding: 20px; max-width: 800px;">
          <!-- Header -->
          <div style="text-align: center; margin-bottom: 30px; border-bottom: 2px solid #333; padding-bottom: 20px;">
            <h1 style="color: #2c5530; font-size: 28px; margin: 0;">Reliable CRM</h1>
            <h2 style="color: #666; font-size: 18px; margin: 10px 0 0 0;">Landscape Proposal</h2>
            <p style="color: #888; font-size: 14px; margin: 5px 0 0 0;">
              Prepared: ${new Date().toLocaleDateString('en-US', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}
            </p>
          </div>

          <!-- Project Info -->
          <div style="margin-bottom: 30px;">
            <h3 style="color: #2c5530; border-bottom: 1px solid #ccc; padding-bottom: 5px;">Project Information</h3>
            <p><strong>Project Name:</strong> ${escapeHtml(estimateData.project_name) || 'Untitled Project'}</p>
            <p><strong>Client:</strong> ${escapeHtml(estimateData.client_name) || 'N/A'}</p>
            <p><strong>Phone:</strong> ${formatPhone(estimateData.client_phone) || 'N/A'}</p>
            <p><strong>Email:</strong> ${escapeHtml(estimateData.client_email) || 'N/A'}</p>
            <p><strong>Job Site Address:</strong> ${escapeHtml(estimateData.job_address) || 'N/A'}</p>
            ${estimateData.first_responder ? '<p><strong>First Responder Discount Applied</strong></p>' : ''}
          </div>

          <!-- Designer Info -->
          ${estimateData.designer_name ? `
          <div style="margin-bottom: 30px;">
            <h3 style="color: #2c5530; border-bottom: 1px solid #ccc; padding-bottom: 5px;">Designer Information</h3>
            <p><strong>Designer:</strong> ${escapeHtml(estimateData.designer_name)}</p>
            ${estimateData.designer_phone ? `<p><strong>Phone:</strong> ${formatPhone(estimateData.designer_phone)}</p>` : ''}
            ${estimateData.designer_email ? `<p><strong>Email:</strong> ${escapeHtml(estimateData.designer_email)}</p>` : ''}
            ${estimateData.designer_his ? `<p><strong>HIS License #:</strong> ${escapeHtml(estimateData.designer_his)}</p>` : ''}
          </div>
          ` : ''}

          <!-- Work Items Table -->
          <div style="margin-bottom: 30px;">
            <h3 style="color: #2c5530; border-bottom: 1px solid #ccc; padding-bottom: 5px; margin-bottom: 15px;">Proposed Work Items</h3>
            <table style="width: 100%; border-collapse: collapse; margin-bottom: 20px;">
              <thead>
                <tr style="background-color: #f5f5f5;">
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Item</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: left;">Category</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Qty</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Unit</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Price</th>
                  <th style="border: 1px solid #ddd; padding: 8px; text-align: right;">Line Total</th>
                </tr>
              </thead>
              <tbody>
                ${estimateData.work_items.filter(item => item.selected).map(item => `
                  <tr>
                    <td style="border: 1px solid #ddd; padding: 8px;">${escapeHtml(item.name)}</td>
                    <td style="border: 1px solid #ddd; padding: 8px;">${escapeHtml(item.category)}</td>
                    <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${item.quantity}</td>
                    <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">${escapeHtml(item.unit)}</td>
                    <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">$${item.price.toLocaleString()}</td>
                    <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">$${item.line_total.toLocaleString()}</td>
                  </tr>
                `).join('')}
              </tbody>
              <tfoot>
                <tr>
                  <td colspan="5" style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold;">Subtotal</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold;">$${subtotal.toLocaleString()}</td>
                </tr>
                ${discounts.map(discount => `
                  <tr>
                    <td colspan="5" style="border: 1px solid #ddd; padding: 8px; text-align: right;">${escapeHtml(discount.label)}</td>
                    <td style="border: 1px solid #ddd; padding: 8px; text-align: right;">$${discount.amount.toLocaleString()}</td>
                  </tr>
                `).join('')}
                <tr style="background-color: #f5f5f5;">
                  <td colspan="5" style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold; font-size: 16px;">Total</td>
                  <td style="border: 1px solid #ddd; padding: 8px; text-align: right; font-weight: bold; font-size: 16px;">$${total.toLocaleString()}</td>
                </tr>
              </tfoot>
            </table>
          </div>

          <!-- Notes -->
          ${estimateData.client_notes ? `
          <div style="margin-bottom: 30px;">
            <h3 style="color: #2c5530; border-bottom: 1px solid #ccc; padding-bottom: 5px;">Project Notes</h3>
            <p>${escapeHtml(estimateData.client_notes)}</p>
          </div>
          ` : ''}

          <!-- Footer -->
          <div style="text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #ccc; color: #666; font-size: 12px;">
            <p>This proposal is valid for 30 days from the date of preparation.</p>
            <p>Thank you for considering Reliable CRM for your landscaping needs!</p>
          </div>
        </div>
      `;

      // Use browser's built-in print functionality to save as PDF
      const printWindow = window.open('', '_blank');
      if (!printWindow) {
        alert('Please allow popups for this site');
        return;
      }
      printWindow.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Proposal - ${escapeHtml(estimateData.project_name) || 'Untitled Project'}</title>
          <style>
            @media print {
              body { margin: 0; }
              @page { margin: 0.5in; }
            }
          </style>
        </head>
        <body>
          ${pdfContent.innerHTML}
        </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      
      // Auto-trigger print dialog
      setTimeout(() => {
        printWindow.print();
      }, 250);

    } catch (error) {
      console.error("Failed to generate PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    }
  };

  // Save estimate
  const handleSave = () => {
    if (!estimateData.project_name.trim()) {
      toast({
        variant: "destructive",
        description: "Please enter a project name before saving."
      });
      return;
    }
    
    const { subtotal, discounts, total } = calculateTotals();
    
    const estimatePayload = {
      project_name: estimateData.project_name,
      client_name: estimateData.client_name || "Draft Client",
      customer_id: selectedCustomer && selectedCustomer !== "none" ? selectedCustomer : null,
      project_id: selectedProject && selectedProject !== "none" ? selectedProject : null,
      work_items: estimateData.work_items || [],
      design_images: estimateData.design_images || [],
      client_phone: estimateData.client_phone || "",
      client_email: estimateData.client_email || "",
      job_address: estimateData.job_address || "",
      client_notes: estimateData.client_notes || "",
      designer_name: estimateData.designer_name || "",
      designer_email: estimateData.designer_email || "",
      designer_phone: estimateData.designer_phone || "",
      designer_his: estimateData.designer_his || "",
      first_responder: estimateData.first_responder || false,
      subtotal,
      discount_amount: discounts.reduce((sum, d) => sum + Math.abs(d.amount), 0),
      total,
      status: "draft"
    };
    
    if (isEditMode) {
      updateEstimateMutation.mutate(estimatePayload);
    } else {
      createEstimateMutation.mutate(estimatePayload);
    }
  };

  const { subtotal, discounts, total } = calculateTotals();

  // Show loading state when fetching estimate in edit mode
  if (isEditMode && estimateLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading estimate...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEditMode ? "Edit Estimate" : "Create New Estimate"}
          </h1>
          <p className="text-gray-600">
            {isEditMode ? "Update your landscaping proposal" : "Create a detailed proposal for your landscaping project"}
          </p>
        </div>
        <div className="flex space-x-2">
          <Button variant="outline" onClick={() => navigate("/estimates")}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            disabled={createEstimateMutation.isPending || updateEstimateMutation.isPending}
          >
            {(createEstimateMutation.isPending || updateEstimateMutation.isPending) 
              ? "Saving..." 
              : isEditMode 
                ? "Update Estimate" 
                : "Save Estimate"}
          </Button>
        </div>
      </div>

      {/* Project Name */}
      <Card>
        <CardContent className="pt-6">
          <div>
            <Label htmlFor="project_name">Project Name</Label>
            <Input
              id="project_name"
              value={estimateData.project_name}
              onChange={(e) => setEstimateData(prev => ({ ...prev, project_name: e.target.value }))}
              placeholder="e.g., Smith Backyard Remodel"
            />
          </div>
        </CardContent>
      </Card>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="client">Client Info</TabsTrigger>
          <TabsTrigger value="items">Work Items</TabsTrigger>
          <TabsTrigger value="preview">Preview</TabsTrigger>
        </TabsList>

        {/* Client Info Tab */}
        <TabsContent value="client" className="space-y-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Client Information */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-base font-medium">Client</CardTitle>
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="first_responder"
                    checked={estimateData.first_responder}
                    onCheckedChange={(checked) => 
                      setEstimateData(prev => ({ ...prev, first_responder: checked }))
                    }
                  />
                  <Label htmlFor="first_responder" className="text-sm">
                    First Responder?
                  </Label>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Select Existing Customer (Optional)</Label>
                  <SearchableSelect
                    items={[
                      { id: 'none', name: 'None', last_name: '- Enter manually' },
                      ...(customersData?.data || [])
                    ]}
                    value={selectedCustomer}
                    onValueChange={handleCustomerChange}
                    getItemLabel={(customer) => {
                      if (customer.id === 'none') return 'None - Enter manually';
                      const firstName = customer.name || '';
                      const lastName = customer.last_name || '';
                      return `${firstName} ${lastName}`.trim() || 'Unnamed Customer';
                    }}
                    placeholder="Select a customer or enter manually"
                    searchPlaceholder="Search customers..."
                    emptyMessage="No customers found"
                  />
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="client_name">Client Name</Label>
                    <Input
                      id="client_name"
                      value={estimateData.client_name}
                      onChange={(e) => setEstimateData(prev => ({ ...prev, client_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="client_phone">Phone</Label>
                    <PhoneInput
                      id="client_phone"
                      value={estimateData.client_phone}
                      onChange={(val) => setEstimateData(prev => ({ ...prev, client_phone: val }))}
                      placeholder="916.232.2332"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="client_email">Email</Label>
                  <Input
                    id="client_email"
                    type="email"
                    value={estimateData.client_email}
                    onChange={(e) => setEstimateData(prev => ({ ...prev, client_email: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label htmlFor="job_address">Job Site Address</Label>
                  <Input
                    id="job_address"
                    value={estimateData.job_address}
                    onChange={(e) => setEstimateData(prev => ({ ...prev, job_address: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label htmlFor="client_notes">Notes</Label>
                  <Textarea
                    id="client_notes"
                    value={estimateData.client_notes}
                    onChange={(e) => setEstimateData(prev => ({ ...prev, client_notes: e.target.value }))}
                    rows={4}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Designer Information */}
            <Card>
              <CardHeader>
                <CardTitle className="text-base font-medium">Designer</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Link to Existing Project (Optional)</Label>
                  <SearchableSelect
                    items={[
                      { id: 'none', name: 'None - Enter manually' },
                      ...(projectsData?.data || [])
                    ]}
                    value={selectedProject}
                    onValueChange={handleProjectChange}
                    getItemLabel={(project) => {
                      if (project.id === 'none') return 'None - Enter manually';
                      return project.name || 'Unnamed Project';
                    }}
                    placeholder="Select a project or enter manually"
                    searchPlaceholder="Search projects..."
                    emptyMessage="No projects found"
                  />
                </div>

                <div>
                  <Label>Select Existing Designer (Optional)</Label>
                  <SearchableSelect
                    items={[
                      { id: 'none', name: 'None', specialty: '- Enter manually' },
                      ...(designersData?.data || [])
                    ]}
                    value={selectedDesigner || "none"}
                    onValueChange={handleDesignerChange}
                    getItemLabel={(designer) => {
                      if (designer.id === 'none') return 'None - Enter manually';
                      const specialty = designer.specialty ? ` - ${designer.specialty}` : ' - Designer';
                      return `${designer.name}${specialty}`;
                    }}
                    placeholder="Select a designer or enter manually"
                    searchPlaceholder="Search designers..."
                    emptyMessage="No designers found"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="designer_name">Designer Name</Label>
                    <Input
                      id="designer_name"
                      value={estimateData.designer_name}
                      onChange={(e) => setEstimateData(prev => ({ ...prev, designer_name: e.target.value }))}
                    />
                  </div>
                  <div>
                    <Label htmlFor="designer_phone">Designer Phone</Label>
                    <PhoneInput
                      id="designer_phone"
                      value={estimateData.designer_phone}
                      onChange={(val) => setEstimateData(prev => ({ ...prev, designer_phone: val }))}
                      placeholder="916.232.2332"
                    />
                  </div>
                </div>
                
                <div>
                  <Label htmlFor="designer_email">Designer Email</Label>
                  <Input
                    id="designer_email"
                    type="email"
                    value={estimateData.designer_email}
                    onChange={(e) => setEstimateData(prev => ({ ...prev, designer_email: e.target.value }))}
                  />
                </div>
                
                <div>
                  <Label htmlFor="designer_his">HIS #</Label>
                  <Input
                    id="designer_his"
                    value={estimateData.designer_his}
                    onChange={(e) => setEstimateData(prev => ({ ...prev, designer_his: e.target.value }))}
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* Work Items Tab */}
        <TabsContent value="items" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Work Items</CardTitle>
              <div className="flex space-x-2">
                <Button 
                  onClick={() => {
                    setNewWorkItem(prev => ({ ...prev, is_custom: false }));
                    setShowAddItemDialog(true);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Work Item
                </Button>
                <Button 
                  variant="outline"
                  onClick={() => {
                    setNewWorkItem(prev => ({ ...prev, is_custom: true }));
                    setShowAddItemDialog(true);
                  }}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Custom Item
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {estimateData.work_items.length > 0 ? (
                <div className="space-y-4">
                  {estimateData.work_items.map((item) => (
                    <div key={item.id} className="border rounded-lg p-4">
                      <div className="flex items-center justify-between mb-4">
                        <div className="flex items-center space-x-2">
                          <Checkbox
                            checked={item.selected}
                            onCheckedChange={() => toggleItemSelection(item.id)}
                          />
                          <div>
                            <h4 className="font-medium">{item.name}</h4>
                            {item.is_custom && <Badge variant="secondary">Custom Item</Badge>}
                          </div>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => removeWorkItem(item.id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                        <div>
                          <Label>Quantity</Label>
                          <Input
                            type="number"
                            min="0"
                            step="1"
                            value={item.quantity}
                            onChange={(e) => updateWorkItem(item.id, 'quantity', Number(e.target.value))}
                          />
                        </div>
                        <div>
                          <Label>Category</Label>
                          <Input value={item.category} disabled />
                        </div>
                        <div>
                          <Label>Unit</Label>
                          <Input value={item.unit} disabled />
                        </div>
                        <div>
                          <Label>Price</Label>
                          <Input
                            type="number"
                            min="0"
                            step="0.01"
                            value={item.price}
                            onChange={(e) => updateWorkItem(item.id, 'price', Number(e.target.value))}
                            disabled={!item.is_custom}
                          />
                        </div>
                        <div>
                          <Label>Total</Label>
                          <div className="text-lg font-medium">
                            ${item.line_total.toLocaleString()}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No work items added yet. Click "Add Work Item" to get started.
                </div>
              )}
              
              {/* Total */}
              <div className="mt-6 pt-4 border-t">
                <div className="text-right">
                  <div className="text-lg font-semibold">
                    Grand Total: ${total.toLocaleString()}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Preview Tab */}
        <TabsContent value="preview" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Proposal Preview</CardTitle>
                <p className="text-sm text-gray-600">
                  Prepared: {new Date().toLocaleDateString('en-US', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })}
                </p>
              </div>
              <div className="flex space-x-2">
                <Button 
                  variant="outline"
                  onClick={() => document.getElementById('design-upload').click()}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Add Design
                </Button>
                <input
                  id="design-upload"
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageUpload}
                  className="hidden"
                />
                <Button onClick={handleExportPDF}>
                  <FileText className="w-4 h-4 mr-2" />
                  Export PDF
                </Button>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Design Gallery */}
              {estimateData.design_images.length > 0 && (
                <div>
                  <h3 className="text-lg font-medium mb-4">Design Images</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {estimateData.design_images.map((image, index) => (
                      <div key={index} className="relative">
                        <img 
                          src={image} 
                          alt={`Design ${index + 1}`}
                          className="w-full h-32 object-cover rounded-lg border"
                        />
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2"
                          onClick={() => removeDesignImage(index)}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Preview Table */}
              <div className="overflow-x-auto">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="border border-gray-300 px-4 py-2 text-left">Item</th>
                      <th className="border border-gray-300 px-4 py-2 text-left">Category</th>
                      <th className="border border-gray-300 px-4 py-2 text-right">Qty</th>
                      <th className="border border-gray-300 px-4 py-2 text-right">Unit</th>
                      <th className="border border-gray-300 px-4 py-2 text-right">Price</th>
                      <th className="border border-gray-300 px-4 py-2 text-right">Line Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {estimateData.work_items.filter(item => item.selected).map((item) => (
                      <tr key={item.id}>
                        <td className="border border-gray-300 px-4 py-2">{item.name}</td>
                        <td className="border border-gray-300 px-4 py-2">{item.category}</td>
                        <td className="border border-gray-300 px-4 py-2 text-right">{item.quantity}</td>
                        <td className="border border-gray-300 px-4 py-2 text-right">{item.unit}</td>
                        <td className="border border-gray-300 px-4 py-2 text-right">${item.price.toLocaleString()}</td>
                        <td className="border border-gray-300 px-4 py-2 text-right">${item.line_total.toLocaleString()}</td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr>
                      <td colSpan="5" className="border border-gray-300 px-4 py-2 text-right font-medium">Subtotal</td>
                      <td className="border border-gray-300 px-4 py-2 text-right font-medium">${subtotal.toLocaleString()}</td>
                    </tr>
                    {discounts.map((discount, index) => (
                      <tr key={index}>
                        <td colSpan="5" className="border border-gray-300 px-4 py-2 text-right">{discount.label}</td>
                        <td className="border border-gray-300 px-4 py-2 text-right">${discount.amount.toLocaleString()}</td>
                      </tr>
                    ))}
                    <tr className="bg-gray-50">
                      <td colSpan="5" className="border border-gray-300 px-4 py-2 text-right font-bold">Total</td>
                      <td className="border border-gray-300 px-4 py-2 text-right font-bold">${total.toLocaleString()}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Add Work Item Dialog */}
      <Dialog open={showAddItemDialog} onOpenChange={setShowAddItemDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>
              {newWorkItem.is_custom ? "Add Custom Item" : "Add Work Item"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {!newWorkItem.is_custom ? (
              <div>
                <Label>Select Item</Label>
                <Select 
                  value={newWorkItem.selected_id || ""} 
                  onValueChange={(value) => {
                    const item = predefinedItems.find(i => i.id === value);
                    if (item) {
                      setNewWorkItem({
                        ...newWorkItem,
                        selected_id: value,
                        name: item.customer_view || item.name,
                        category: item.category,
                        unit: item.unit,
                        price: item.price,
                        base_price: item.price, // Store the minimum price from database
                        internal_name: item.name,
                        commission: item.commission || 0,
                        labor: item.labor || 0
                      });
                    }
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select an item..." />
                  </SelectTrigger>
                  <SelectContent>
                    {workItemsLoading ? (
                      <SelectItem value="loading" disabled>Loading items...</SelectItem>
                    ) : predefinedItems.length === 0 ? (
                      <SelectItem value="empty" disabled>No items available</SelectItem>
                    ) : (
                      predefinedItems.map((item) => (
                        <SelectItem key={item.id} value={item.id}>
                          {item.customer_view || item.name} - {item.category} (${item.price}/{item.unit})
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <>
                <div>
                  <Label>Item Name</Label>
                  <Input
                    value={newWorkItem.name}
                    onChange={(e) => setNewWorkItem(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Special Pergola Detail"
                  />
                </div>
                <div>
                  <Label>Category</Label>
                  <Input
                    value={newWorkItem.category}
                    onChange={(e) => setNewWorkItem(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="e.g., Carpentry"
                  />
                </div>
                <div>
                  <Label>Unit</Label>
                  <Input
                    value={newWorkItem.unit}
                    onChange={(e) => setNewWorkItem(prev => ({ ...prev, unit: e.target.value }))}
                    placeholder="e.g., ea., sqft"
                  />
                </div>
              </>
            )}
            
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Quantity</Label>
                <Input
                  type="number"
                  min="1"
                  value={newWorkItem.quantity}
                  onChange={(e) => setNewWorkItem(prev => ({ ...prev, quantity: Number(e.target.value) }))}
                />
              </div>
              <div>
                <Label>Price</Label>
                <Input
                  type="number"
                  min={newWorkItem.base_price || 0}
                  step="0.01"
                  value={newWorkItem.price}
                  onChange={(e) => setNewWorkItem(prev => ({ ...prev, price: Number(e.target.value) }))}
                  className={newWorkItem.base_price > 0 && newWorkItem.price < newWorkItem.base_price ? "border-red-500" : ""}
                />
                {newWorkItem.base_price > 0 && (
                  <p className="text-xs text-gray-500 mt-1">
                    Base price: ${newWorkItem.base_price.toFixed(2)} (minimum)
                  </p>
                )}
                {newWorkItem.base_price > 0 && newWorkItem.price < newWorkItem.base_price && (
                  <p className="text-xs text-red-600 mt-1">
                    Price must be at least ${newWorkItem.base_price.toFixed(2)}
                  </p>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddItemDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={addWorkItem}
              disabled={
                !newWorkItem.name || 
                newWorkItem.price <= 0 || 
                (newWorkItem.base_price > 0 && newWorkItem.price < newWorkItem.base_price)
              }
            >
              Add Item
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}